// ============================================================================
// UserAvatar.tsx — ЕДИНЫЙ компонент аватара пользователя (SRP/SSoT).
//   • если задан fileId (avatarUrl — id файла) → показывает картинку
//     (dataURL из avatarStore — кэш + событие обновления);
//   • иначе — цветной круг по id сущности (avatarBg/avatarFg, детерминированно)
//     с инициалами имени.
// Подписан на avatarStore → при смене аватара в профиле обновляется ВЕЗДЕ
// (шапка/сайдбар/чат/публикация) без ручной инвалидации.
// ============================================================================

import { useEffect, useState } from 'react';
import { avatarBg, avatarFg } from '@/lib/avatarColor';
import { getAvatar, subscribeAvatars, resolveAvatarFileId } from '@/lib/avatarStore';

export interface UserAvatarProps {
  /** id сущности (для цветного круга) */
  uid: string;
  /** id файла-аватара (avatarUrl) — картинка вместо круга */
  fileId?: string;
  /** имя пользователя (для инициалов) */
  name?: string;
  /** размер в px (по умолчанию 32) */
  size?: number;
  className?: string;
  /** инициалы всегда (не показывать файл) — напр., в профиле с превью */
  forceInitials?: boolean;
}

export function UserAvatar({ uid, fileId, name, size = 32, className = '', forceInitials = false }: UserAvatarProps) {
  // Резолвим актуальный fileId: после смены аватара в профиле uidFileId
  // (avatarStore) актуальнее, чем загруженный ранее список сущностей.
  const effectiveFileId = resolveAvatarFileId(uid, fileId || '');
  const [src, setSrc] = useState<string | null>(() => (forceInitials ? null : getAvatar(effectiveFileId)));

  useEffect(() => {
    setSrc(forceInitials ? null : getAvatar(effectiveFileId));
    if (forceInitials || !effectiveFileId) return;
    return subscribeAvatars(() => setSrc(getAvatar(effectiveFileId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFileId, forceInitials]);

  const initials = (nm: string) => {
    const s = nm.trim();
    if (!s) return '?';
    const parts = s.split(/\s+/);
    if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  };

  const base = 'rounded-full shrink-0 border shadow-sm flex items-center justify-center overflow-hidden';
  const style: React.CSSProperties = { width: size, height: size };

  if (!forceInitials && src) {
    return <img src={src} alt={name || ''} title={name} className={`${base} object-cover ${className}`} style={style} />;
  }

  return (
    <span
      className={`${base} text-xs font-bold ${className}`}
      style={{ ...style, background: avatarBg(uid, false), color: avatarFg(uid, false) }}
    >
      {initials(name || uid || '?')}
    </span>
  );
}
